import type { ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../lib/auth.js';
import { MobileBottomNav } from './MobileNav.js';

function MoreRow({ to, href, icon, label, danger, onClick }: { to?: string; href?: string; icon: ReactNode; label: string; danger?: boolean; onClick?: () => void }) {
  const content = (
    <>
      <span className={`flex size-8 shrink-0 items-center justify-center ${danger ? 'text-danger' : 'text-text-secondary'}`}>{icon}</span>
      <span className={`flex-1 text-[15px] ${danger ? 'font-semibold text-danger' : 'text-ink'}`}>{label}</span>
      {!danger && (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="text-text-faint" aria-hidden="true">
          <path d="m9 6 6 6-6 6" />
        </svg>
      )}
    </>
  );
  const className = 'flex h-13 items-center gap-3 rounded-control border border-border bg-surface px-3.5 no-underline';
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${className} text-left`}>
        {content}
      </button>
    );
  }
  if (href) {
    return (
      <a href={href} className={className}>
        {content}
      </a>
    );
  }
  return (
    <Link to={to} className={className}>
      {content}
    </Link>
  );
}

/** No design artboard covers this (design/orion-crm-design/SCREENS.md has no
 * Mobile More screen), but leaving it a dead-end "not built yet" placeholder
 * meant Sign out and Security settings — real, already-working features —
 * were unreachable from a phone. Reuses the exact same actions as the
 * desktop AvatarMenu (apps/web/src/app/shell.tsx) rather than inventing new
 * mobile-only ones. */
export function MobileMorePage() {
  const { session, hasPermission, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    await navigate({ to: '/login' });
  };

  return (
    <div className="flex h-dvh flex-col bg-ground text-ink">
      <header className="flex h-14 shrink-0 items-center px-4">
        <h1 className="text-[16px] font-semibold">More</h1>
      </header>
      <main className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        <div className="mb-4 flex items-center gap-3 rounded-control border border-border bg-surface px-3.5 py-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-avatar-indigo text-sm font-semibold text-white">
            {(session?.user.displayName ?? '?').slice(0, 2).toUpperCase()}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] font-semibold">{session?.user.displayName}</span>
            <span className="block truncate text-[13px] text-text-muted">{session?.user.email}</span>
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <MoreRow
            to="/account/security"
            label="Security settings"
            icon={
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="4" y="11" width="16" height="9" rx="2" />
                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
              </svg>
            }
          />
          {hasPermission('admin.users.view') && (
            <MoreRow
              to="/admin"
              label="Administration"
              icon={
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
                </svg>
              }
            />
          )}
          <MoreRow
            href="mailto:support@oes.com"
            label="Help & docs"
            icon={
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2 1.8-2 3.5M12 17h.01" />
              </svg>
            }
          />
          <MoreRow
            onClick={handleLogout}
            danger
            label="Sign out"
            icon={
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="M16 17l5-5-5-5M21 12H9" />
              </svg>
            }
          />
        </div>
      </main>
      <MobileBottomNav active="more" />
    </div>
  );
}
